import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import ManualFindBar from '../ManualFindBar';
import ManualHeader from '../ManualHeader';
import ManualNav from '../ManualNav';
import ManualPagination from '../ManualPagination';
import { MANUAL_SEARCH_ENDPOINT } from '../ManualSearch/ManualSearch';
import styles from './ManualLayout.module.scss';

// Rich text images come from Wagtail markup, so they are enhanced from the
// outside rather than rendered as buttons.
const ZOOMABLE = 'img[data-zoomable], img.richtext-image';

const ManualLayout = ({
    header = {},
    manual = null,
    navigation = [],
    activeAnchor = '',
    pagination = {},
    isWide = false,
    children = null,
}) => {
    const router = useRouter();
    const mainRef = useRef(null);
    const [zoomedImage, setZoomedImage] = useState(null);

    useEffect(() => {
        mainRef.current?.querySelectorAll(ZOOMABLE).forEach((image) => {
            if (image.closest('a')) {
                return;
            }

            image.tabIndex = 0;
            image.setAttribute('role', 'button');
            image.setAttribute('aria-haspopup', 'dialog');
        });
    });

    // Links in rich text are plain anchors, so without this every link to
    // another chapter reloads the whole page.
    const followLink = (event) => {
        const link = event.target.closest?.('a[href]');

        if (
            !link ||
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            (link.target && link.target !== '_self') ||
            link.hasAttribute('download')
        ) {
            return false;
        }

        const url = new URL(link.href);
        const isSamePage = url.pathname === window.location.pathname;

        // Documents and media are served by Wagtail, not by a Next.js page.
        if (
            url.origin !== window.location.origin ||
            url.pathname.startsWith('/wt/') ||
            (isSamePage && url.hash)
        ) {
            return false;
        }

        event.preventDefault();
        router.push(url.pathname + url.search + url.hash);
        return true;
    };

    const searchQuery =
        typeof router.query.q === 'string' ? router.query.q.trim() : '';
    const [matchedHrefs, setMatchedHrefs] = useState([]);
    const manualId = manual?.id;

    useEffect(() => {
        if (!searchQuery || !manualId) {
            setMatchedHrefs([]);
            return undefined;
        }

        const controller = new AbortController();
        const params = new URLSearchParams({
            manual: manualId,
            q: searchQuery,
        });

        fetch(`${MANUAL_SEARCH_ENDPOINT}?${params}`, {
            signal: controller.signal,
        })
            .then((response) => response.json())
            .then((data) => setMatchedHrefs(data.hrefs || []))
            .catch(() => {});

        return () => controller.abort();
    }, [searchQuery, manualId]);

    const clearSearch = () => {
        const { hash, pathname } = window.location;
        router.replace(pathname + hash, undefined, {
            shallow: true,
            scroll: false,
        });
    };

    const handleClick = (event) => {
        if (!followLink(event)) {
            zoom(event);
        }
    };

    const zoom = (event) => {
        const image = event.target.closest?.(ZOOMABLE);

        if (!image || image.closest('a')) {
            return;
        }

        if (event.type === 'keydown') {
            if (event.key !== 'Enter' && event.key !== ' ') {
                return;
            }

            event.preventDefault();
        }

        setZoomedImage({
            src: image.currentSrc || image.src,
            alt: image.alt,
            width: image.naturalWidth,
            height: image.naturalHeight,
        });
    };

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return undefined;
        }

        const root = document.documentElement;
        const previous = root.style.scrollBehavior;
        root.style.scrollBehavior = 'smooth';

        return () => {
            root.style.scrollBehavior = previous;
        };
    }, []);

    return (
        <div
            className={classNames(styles['ManualLayout'], {
                [styles['ManualLayout--Wide']]: isWide,
            })}>
            <div className={styles['ManualLayout__Header']}>
                <ManualHeader {...header} manualId={manual?.id} />
            </div>

            <div className={styles['ManualLayout__Container']}>
                {!!navigation.length && (
                    <div className={styles['ManualLayout__Nav']}>
                        <ManualNav
                            chapters={navigation}
                            activeAnchor={activeAnchor}
                            matchedHrefs={matchedHrefs}
                            searchQuery={searchQuery}
                        />
                    </div>
                )}

                <main
                    ref={mainRef}
                    className={styles['ManualLayout__Main']}
                    onClick={handleClick}
                    onKeyDown={zoom}>
                    {children}

                    {(!!pagination.prev || !!pagination.next) && (
                        <div className={styles['ManualLayout__Pagination']}>
                            <ManualPagination {...pagination} />
                        </div>
                    )}
                </main>
            </div>

            {!!searchQuery && (
                <ManualFindBar
                    key={router.asPath.split('#')[0]}
                    query={searchQuery}
                    containerRef={mainRef}
                    onClose={clearSearch}
                />
            )}

            <Lightbox
                className={styles['ManualLayout__Lightbox']}
                open={!!zoomedImage}
                close={() => setZoomedImage(null)}
                slides={zoomedImage ? [zoomedImage] : []}
                plugins={[Zoom]}
                zoom={{ maxZoomPixelRatio: 3 }}
                controller={{ closeOnBackdropClick: true }}
                carousel={{
                    finite: true,
                    // The library caps images at their natural size, so small
                    // sources would open smaller than they appear on the page.
                    imageProps: {
                        style: {
                            width: '80vw',
                            height: '80vh',
                            maxWidth: 'none',
                            maxHeight: 'none',
                            objectFit: 'contain',
                        },
                    },
                }}
                render={{
                    buttonPrev: () => null,
                    buttonNext: () => null,
                }}
            />
        </div>
    );
};

ManualLayout.propTypes = {
    header: PropTypes.object,
    manual: PropTypes.object,
    navigation: PropTypes.array,
    activeAnchor: PropTypes.string,
    pagination: PropTypes.object,
    isWide: PropTypes.bool,
    children: PropTypes.node,
};

export default ManualLayout;
